import dayjs from "dayjs";
import React from "react";
import { MessageStatus } from "./hooks/useChatContext";
import cx from "classnames";
import styles from "./MessageBox.module.scss";
import { DoubleCheckIcon } from "@/components/Icon";
import Typography from "@/components/Typography";
import RichContentPreview from "@/components/RichContentPreview";
import { WarningIcon } from "@/components/Icon";
import { SocialAvatar } from "@/components/Social";
import { usePublicProfileQuery } from "@/fetch/profiles/traveller";
import { CircularProgress } from "../Loader";

const isSameDay = (date1?: Date, date2?: Date) => {
  if (!date2) {
    return false;
  }
  const currentCreatedAt = dayjs(date1);
  const diffCreatedAt = dayjs(date2);
  if (!currentCreatedAt.isValid() || !diffCreatedAt.isValid()) {
    return false;
  }
  return currentCreatedAt.isSame(diffCreatedAt, "day");
};

const Box: React.FC<{
  status?: MessageStatus;
  text?: string | null;
  leftBubble?: boolean;
  date?: Date | null;
  profilePicturePath?: string;
  name?: string;
}> = ({
  status = "sent",
  text,
  leftBubble,
  date,
  profilePicturePath,
  name,
}) => {
  const time = dayjs(date).format("HH:mm");
  return (
    <div className={styles.messageContainer}>
      <SocialAvatar
        size="s"
        cloudinaryPath={profilePicturePath}
        className={styles.avatar}
      />
      <div className={styles.contentContainer}>
        <Typography variant="body1" className={styles.nameContainer}>
          {name}
        </Typography>
        <RichContentPreview content={text || ""} className="fs-exclude" />
        {!leftBubble && status === "notSent" && (
          <WarningIcon
            color="error"
            fontSize="inherit"
            variant="outlined"
            className={styles.notSentIcon}
          />
        )}
      </div>

      <span className={styles.messageTime}>
        {/* {!leftBubble && status === "sending" && (
          <CircularProgress size="xxs" className={styles.sendingSpinner} />
        )}
        {!leftBubble && status === "notSent" && (
          <WarningIcon
            color="error"
            fontSize="inherit"
            variant="outlined"
            className={styles.notSentIcon}
          />
        )}
        {!leftBubble && status === "sent" && (
          <DoubleCheckIcon color="info" fontSize="small" />
        )}
        {!leftBubble && status === "delivered" && (
          <DoubleCheckIcon color="primary" fontSize="small" />
        )} */}
        <Typography variant="caption" color={"text.secondary"}>
          {time}
        </Typography>
      </span>
    </div>
  );
};

const MessageBox: React.FC<{
  leftBubble?: boolean;
  previousMessageCreatedDate?: Date | null;
  text?: string | null;
  date?: Date | null;
  status?: MessageStatus;
  senderId?: number;
}> = ({
  leftBubble,
  previousMessageCreatedDate,
  text,
  date,
  status,
  senderId,
}) => {
  const sameDay =
    date && previousMessageCreatedDate
      ? isSameDay(date, previousMessageCreatedDate)
      : false;
  const messageDate = dayjs(date).format("D MMM YYYY");
  let formattedDate;
  const { data: publicProfile } = usePublicProfileQuery({ cid: senderId });
  const userProfile = publicProfile?.[0];
  const profilePicturePath = userProfile?.s3_profile_pic;
  const name = userProfile?.nickname;

  if (dayjs(date).isToday()) formattedDate = "Today";
  else if (dayjs(date).isYesterday()) formattedDate = "Yesterday";
  else {
    formattedDate = messageDate;
  }

  return (
    <>
      {!sameDay && (
        <Typography
          color="text.secondary"
          variant="body2"
          className={styles.messagesDate}
        >
          {formattedDate}
        </Typography>
      )}

      <Box
        text={text}
        leftBubble={leftBubble}
        date={date}
        status={status}
        profilePicturePath={profilePicturePath}
        name={name}
      />
    </>
  );
};

export default MessageBox;
